import type { SiteData, Service } from '../../shared/types/site.js';
import { $, escapeHtml } from '../utils/index.js';
import { getServiceImageUrl, iconSvg } from '../constants.js';
import type { Lang } from '../i18n.js';
import { getLanguage, t } from '../i18n.js';

type ServiceDetailContent = {
  intro: string;
  meta: string[];
  approachIntro: string;
  approachSteps: Array<{ title: string; description: string }>;
  lifecycleIntro: string;
  lifecycleSteps: Array<{ title: string; description: string }>;
};

const SERVICE_DETAIL_LIBRARY: Record<Lang, Record<string, ServiceDetailContent>> = {
  en: {
    'web-development': {
      intro:
        'We design and launch web platforms that are fast, resilient, and built for growth.',
      meta: ['Product direction', 'Design systems', 'Full-stack execution'],
      approachIntro:
        'We align strategy, UX, and architecture in tight cycles to move quickly without losing quality.',
      approachSteps: [
        { title: 'Discovery session', description: 'Clarify goals, users, and constraints.' },
        { title: 'System design + UX', description: 'Define architecture, data model, and flows.' },
        { title: 'Build + QA', description: 'Deliver in iterations with testing and review.' },
        { title: 'Launch + improve', description: 'Measure, refine, and scale.' },
      ],
      lifecycleIntro:
        'From MVP to scale, execution stays predictable, transparent, and focused on outcomes.',
      lifecycleSteps: [
        { title: 'Sprint planning', description: 'Prioritize features with clear acceptance criteria.' },
        { title: 'Execution', description: 'Build with modern stacks and dependable CI/CD.' },
        { title: 'Quality assurance', description: 'Validate security, speed, and accessibility.' },
        { title: 'Growth + optimization', description: 'Improve conversion, retention, and reliability.' },
      ],
    },
    'mobile-app': {
      intro:
        'We build mobile apps that feel native, perform beautifully, and keep users engaged.',
      meta: ['iOS + Android', 'Cross-platform', 'Store readiness'],
      approachIntro:
        'From prototype to release, we focus on usability, speed, and stability.',
      approachSteps: [
        { title: 'Experience mapping', description: 'Define critical journeys and key moments.' },
        { title: 'UI system', description: 'Create a cohesive interface with reusable components.' },
        { title: 'Mobile execution', description: 'Launch native or cross-platform apps with care.' },
        { title: 'Release + support', description: 'Prepare listings, QA, and updates.' },
      ],
      lifecycleIntro:
        'Stable releases backed by clear milestones and reliable QA.',
      lifecycleSteps: [
        { title: 'Prototype', description: 'Clickable flows to align quickly.' },
        { title: 'Build', description: 'Feature execution with analytics and telemetry.' },
        { title: 'Store launch', description: 'Compliance, assets, and rollout planning.' },
        { title: 'Ongoing support', description: 'Crash monitoring, updates, and enhancements.' },
      ],
    },
    'creative-design-uiux': {
      intro:
        'We design interfaces that are clear, elegant, and grounded in real user insight.',
      meta: ['UX research', 'UI systems', 'Design handoff'],
      approachIntro:
        'We prioritize clarity and flow so users move with confidence.',
      approachSteps: [
        { title: 'Research', description: 'Learn goals, pain points, and context.' },
        { title: 'UX flow design', description: 'Map journeys and reduce friction.' },
        { title: 'Visual system', description: 'Define typography, color, and components.' },
        { title: 'Design handoff', description: 'Deliver clear specs for implementation.' },
      ],
      lifecycleIntro:
        'We keep design flexible so you can scale, test, and evolve quickly.',
      lifecycleSteps: [
        { title: 'Exploration', description: 'Directions, concepts, and tone.' },
        { title: 'Prototyping', description: 'High-fidelity screens and interactions.' },
        { title: 'Validation', description: 'Usability testing and iteration.' },
        { title: 'Systemization', description: 'Reusable patterns for growth.' },
      ],
    },
    consulting: {
      intro:
        'We help leaders make confident technical decisions with practical roadmaps.',
      meta: ['Audit + roadmap', 'Team enablement', 'Execution oversight'],
      approachIntro:
        'We assess, align, and guide execution so teams focus on the right priorities.',
      approachSteps: [
        { title: 'Assessment', description: 'Review systems, risks, and opportunities.' },
        { title: 'Roadmap', description: 'Define execution with measurable milestones.' },
        { title: 'Execution support', description: 'Hands-on guidance for teams and partners.' },
        { title: 'Optimization', description: 'Improve cost, performance, and maintainability.' },
      ],
      lifecycleIntro:
        'We bring structure to complex initiatives so leadership has clarity throughout.',
      lifecycleSteps: [
        { title: 'Discovery', description: 'Stakeholder interviews and system audit.' },
        { title: 'Planning', description: 'Scope, budget, and execution plan.' },
        { title: 'Monitoring', description: 'Weekly check-ins and KPI tracking.' },
        { title: 'Stabilization', description: 'Process improvements and team handoff.' },
      ],
    },
    'maintenance-and-support': {
      intro:
        'We keep your product stable, secure, and fast through proactive care.',
      meta: ['Monitoring', 'Security updates', 'SLA support'],
      approachIntro:
        'We combine fast response with long-term reliability work.',
      approachSteps: [
        { title: 'System intake', description: 'Review stack, dependencies, and risks.' },
        { title: 'Monitoring setup', description: 'Alerting, logs, and baselines.' },
        { title: 'Routine upkeep', description: 'Patches, refactors, and security checks.' },
        { title: 'Incident response', description: 'Fast triage and clear communication.' },
      ],
      lifecycleIntro:
        'We move from reactive fixes to proactive stability and predictable operations.',
      lifecycleSteps: [
        { title: 'Baseline', description: 'Audit uptime, performance, and security.' },
        { title: 'Stabilize', description: 'Fix urgent issues and harden systems.' },
        { title: 'Optimize', description: 'Improve cost, performance, and resiliency.' },
        { title: 'Evolve', description: 'Plan upgrades and improvements.' },
      ],
    },
    'ai-futuristic': {
      intro:
        'We apply AI where it delivers value: automation, insight, and new capability.',
      meta: ['ML strategy', 'Data pipelines', 'AI prototypes'],
      approachIntro:
        'We start with real use cases, strong data, and responsible deployment.',
      approachSteps: [
        { title: 'Use-case discovery', description: 'Identify high-impact targets.' },
        { title: 'Data readiness', description: 'Prepare clean, structured pipelines.' },
        { title: 'Modeling + prototyping', description: 'Train, validate, and iterate.' },
        { title: 'Deployment', description: 'Ship safely with monitoring and oversight.' },
      ],
      lifecycleIntro:
        'AI adoption works best with steady progress and clear evaluation.',
      lifecycleSteps: [
        { title: 'Proof of value', description: 'Validate impact with a focused pilot.' },
        { title: 'Integration', description: 'Embed models into workflows.' },
        { title: 'Quality monitoring', description: 'Track drift, bias, and performance.' },
        { title: 'Scale', description: 'Expand use cases with governance.' },
      ],
    },
  },
  sq: {
    'web-development': {
      intro:
        'Dizajnojmë dhe dorëzojmë platforma uebi të shpejta, të qëndrueshme dhe të ndërtuara për rritje.',
      meta: ['Drejtim produkti', 'Sisteme dizajni', 'Dorëzim full-stack'],
      approachIntro:
        'Bashkojmë strategjinë, UX-in dhe inxhinierinë në cikle të shkurtra për shpejtësi pa kompromis.',
      approachSteps: [
        { title: 'Sesion zbulimi', description: 'Sqarojmë qëllimet, përdoruesit dhe kufizimet.' },
        { title: 'Dizajn sistemi + UX', description: 'Definojmë arkitekturën, të dhënat dhe rrjedhat.' },
        { title: 'Ndërtim + QA', description: 'Dorëzim iterativ me testim dhe rishikime.' },
        { title: 'Lançim + përmirësim', description: 'Matje, optimizim dhe shkallëzim.' },
      ],
      lifecycleIntro:
        'Nga MVP te shkallëzimi, dorëzimi mbetet i parashikueshëm dhe transparent.',
      lifecycleSteps: [
        { title: 'Planifikim sprintesh', description: 'Prioritetet dhe kriteret e pranimit janë të qarta.' },
        { title: 'Inxhinieri', description: 'Ndërtojmë me stack modern dhe CI/CD.' },
        { title: 'Sigurim cilësie', description: 'Vërtetojmë sigurinë, shpejtësinë dhe aksesueshmërinë.' },
        { title: 'Optimizim rritjeje', description: 'Përmirësojmë konvertimin, retention dhe besueshmërinë.' },
      ],
    },
    'mobile-app': {
      intro:
        'Ndërtojmë aplikacione mobile me ndjesi native, performancë të lartë dhe angazhim të qëndrueshëm.',
      meta: ['iOS + Android', 'Cross-platform', 'Gati për Store'],
      approachIntro:
        'Nga prototipi në publikim, fokusohemi te përdorshmëria, shpejtësia dhe stabiliteti.',
      approachSteps: [
        { title: 'Hartë përvoje', description: 'Përcaktojmë udhëtimet kyçe.' },
        { title: 'Sistem UI', description: 'Krijojmë ndërfaqe kohezive me komponentë.' },
        { title: 'Dorëzim mobile', description: 'Aplikacione native ose cross-platform me kujdes.' },
        { title: 'Publikim + mbështetje', description: 'Përgatitje, QA dhe përditësime.' },
      ],
      lifecycleIntro:
        'Publikime të qëndrueshme me milestone të qarta dhe QA të besueshme.',
      lifecycleSteps: [
        { title: 'Prototip', description: 'Flukse klikues për alinim të shpejtë.' },
        { title: 'Ndërtim', description: 'Dorëzim veçorish me analitikë.' },
        { title: 'Publikim në Store', description: 'Përputhshmëri, asete dhe rollout.' },
        { title: 'Mbështetje e vazhdueshme', description: 'Monitorim crash-esh dhe përditësime.' },
      ],
    },
    'creative-design-uiux': {
      intro:
        'Krijojmë ndërfaqe të qarta dhe elegante, të bazuara në insight real të përdoruesit.',
      meta: ['Kërkim UX', 'Sisteme UI', 'Handoff dizajni'],
      approachIntro:
        'Prioriteti ynë është qartësia dhe rrjedha e natyrshme e përdorimit.',
      approachSteps: [
        { title: 'Kërkim', description: 'Mësojmë qëllimet dhe kontekstin.' },
        { title: 'Flukse UX', description: 'Hartojmë udhëtimet dhe heqim friksionin.' },
        { title: 'Sistem vizual', description: 'Definojmë tipografi, ngjyra dhe komponentë.' },
        { title: 'Handoff dizajni', description: 'Specifikime të qarta për zhvillim.' },
      ],
      lifecycleIntro:
        'Dizajni mbetet fleksibël që produkti të rritet dhe të testojë shpejt.',
      lifecycleSteps: [
        { title: 'Eksplorim', description: 'Drejtim, koncepte dhe tonalitet.' },
        { title: 'Prototipim', description: 'Ekrane high-fidelity dhe ndërveprime.' },
        { title: 'Validim', description: 'Teste përdorshmërie dhe iterime.' },
        { title: 'Sistemizim', description: 'Modele të ripërdorshme për rritje.' },
      ],
    },
    consulting: {
      intro:
        'Ndihmojmë liderët të marrin vendime teknike të sigurta me roadmap praktik.',
      meta: ['Auditim + roadmap', 'Aktivizim ekipi', 'Mbikëqyrje dorëzimi'],
      approachIntro:
        'Vlerësojmë, alinojmë dhe udhëzojmë ekzekutimin që ekipi të fokusohet në prioritetet e duhura.',
      approachSteps: [
        { title: 'Vlerësim', description: 'Rishikim i sistemeve, rreziqeve dhe mundësive.' },
        { title: 'Roadmap', description: 'Plan dorëzimi me milestone të matshme.' },
        { title: 'Mbështetje ekzekutimi', description: 'Udhëzim praktik për ekipet dhe partnerët.' },
        { title: 'Optimizim', description: 'Përmirësim i kostos, performancës dhe mirëmbajtjes.' },
      ],
      lifecycleIntro:
        'Sjellim strukturë në iniciativa komplekse për qartësi në çdo fazë.',
      lifecycleSteps: [
        { title: 'Zbulim', description: 'Intervista me palët dhe auditim sistemi.' },
        { title: 'Planifikim', description: 'Shtrirje, buxhet dhe plan dorëzimi.' },
        { title: 'Monitorim', description: 'Takime javore dhe KPI.' },
        { title: 'Stabilizim', description: 'Përmirësime procesesh dhe handoff.' },
      ],
    },
    'maintenance-and-support': {
      intro:
        'Mbajmë produktin tuaj të qëndrueshëm, të sigurt dhe të shpejtë me kujdes proaktiv.',
      meta: ['Monitorim', 'Përditësime sigurie', 'Mbështetje SLA'],
      approachIntro:
        'Kombinojmë reagim të shpejtë me përmirësime afatgjata të besueshmërisë.',
      approachSteps: [
        { title: 'Analizë sistemi', description: 'Kuptojmë stack-un, varësitë dhe rreziqet.' },
        { title: 'Vendosje monitorimi', description: 'Alerting, logs dhe baseline performance.' },
        { title: 'Mirëmbajtje rutinë', description: 'Patches, refactor dhe rishikime sigurie.' },
        { title: 'Reagim ndaj incidenteve', description: 'Triage i shpejtë dhe komunikim i qartë.' },
      ],
      lifecycleIntro:
        'Kalojmë nga mbështetje reaktive në stabilitet proaktiv dhe operacione të parashikueshme.',
      lifecycleSteps: [
        { title: 'Bazë', description: 'Auditim i uptime, performance dhe sigurisë.' },
        { title: 'Stabilizim', description: 'Rregullim i çështjeve urgjente.' },
        { title: 'Optimizim', description: 'Përmirësim i kostos, performancës dhe reziliencës.' },
        { title: 'Evoluim', description: 'Planifikim i upgrade-ve dhe përmirësimeve.' },
      ],
    },
    'ai-futuristic': {
      intro:
        'Aplikojmë AI aty ku sjell vlerë: automatizim, insight dhe kapacitete të reja.',
      meta: ['Strategji ML', 'Pipeline të dhënash', 'Prototipe AI'],
      approachIntro:
        'Nisim me raste reale, të dhëna të forta dhe vendosje të përgjegjshme.',
      approachSteps: [
        { title: 'Zbulim rastesh', description: 'Identifikojmë objektiva me impakt.' },
        { title: 'Gati të dhënat', description: 'Përgatisim pipeline të pastra.' },
        { title: 'Modelim + prototip', description: 'Trajnojmë, validon dhe iteron.' },
        { title: 'Vendosje', description: 'Dorëzim i sigurt me monitorim.' },
      ],
      lifecycleIntro:
        'Adoptimi i AI funksionon më mirë me progres të qëndrueshëm dhe vlerësim të qartë.',
      lifecycleSteps: [
        { title: 'Vlerë e provuar', description: 'Validim i impaktit me pilot të fokusuar.' },
        { title: 'Integrim', description: 'Fusim modelet në rrjedha pune.' },
        { title: 'Monitorim cilësie', description: 'Ndjekje e drift-it, bias-it dhe performancës.' },
        { title: 'Shkallëzim', description: 'Zgjerim i përdorimit me qeverisje.' },
      ],
    },
  },
};

function buildFallbackContent(service: Service, lang: Lang): ServiceDetailContent {
  if (lang === 'sq') {
    return {
      intro: service.description,
      meta: ['Zbulim', 'Dorëzim', 'Mbështetje'],
      approachIntro:
        'Planifikojmë çdo angazhim me qartësi objektivash, plan dorëzimi dhe rezultate të matshme.',
      approachSteps: [
        { title: 'Zbulim', description: 'Kuptojmë qëllimet, përdoruesit dhe kufizimet.' },
        { title: 'Planifikim', description: 'Përcaktojmë shtrirjen, milestone dhe afatin.' },
        { title: 'Ekzekutim', description: 'Dorëzim me feedback iterativ dhe kontrolle cilësie.' },
        { title: 'Lançim', description: 'Nisje me besim dhe mbështetje afatgjatë.' },
      ],
      lifecycleIntro:
        'Modeli ynë i dorëzimit është i dizajnuar të rritet me ju, nga nisja në rritje.',
      lifecycleSteps: [
        { title: 'Plan', description: 'Alinim i ekipit dhe roadmap-it.' },
        { title: 'Ndërtim', description: 'Dorëzim i shpejtë dhe i besueshëm.' },
        { title: 'Stabilizim', description: 'Rregullim i problemeve dhe përmirësim performance.' },
        { title: 'Rritje', description: 'Shtim veçorish dhe optimizim.' },
      ],
    };
  }
  return {
    intro: service.description,
    meta: ['Discovery', 'Execution', 'Support'],
    approachIntro:
      'We plan each engagement with clarity on goals, a transparent execution plan, and measurable outcomes.',
    approachSteps: [
      { title: 'Discovery', description: 'Understand your goals, users, and constraints.' },
      { title: 'Planning', description: 'Define scope, milestones, and launch timeline.' },
      { title: 'Execution', description: 'Build with iterative feedback and quality checks.' },
      { title: 'Launch', description: 'Go live with confidence and long-term support.' },
    ],
    lifecycleIntro:
      'Our model is designed to scale with you, from initial launch to long-term growth.',
    lifecycleSteps: [
      { title: 'Plan', description: 'Align team and roadmap.' },
      { title: 'Build', description: 'Move quickly and reliably.' },
      { title: 'Stabilize', description: 'Fix issues and improve performance.' },
      { title: 'Grow', description: 'Add features and optimize.' },
    ],
  };
}

export function renderServiceDetail(siteData: SiteData, slug: string): void {
  const el = $('#page-service-detail');
  if (!el) return;

  const lang = getLanguage();
  const services = siteData.services ?? [];
  const service = services.find((s) => s.slug === slug);
  if (!service) {
    el.innerHTML = `
      <section class="page-hero page-hero-split">
        <div class="container page-hero-grid">
          <div class="animate-on-scroll">
            <p class="page-hero-label">${t('service.detail.label')}</p>
            <h1 class="page-hero-title">${t('service.detail.notfound.title')}</h1>
            <p class="page-hero-intro">${t('service.detail.notfound.body')}</p>
            <a href="/services" class="btn-primary">${t('service.detail.notfound.cta')}</a>
          </div>
        </div>
      </section>
    `;
    return;
  }

  const detail = SERVICE_DETAIL_LIBRARY[lang]?.[service.slug] ?? buildFallbackContent(service, lang);
  el.innerHTML = `
    <section class="page-hero page-hero-split service-detail-hero">
      <div class="container page-hero-grid">
        <div class="animate-on-scroll">
          <p class="page-hero-label">${t('service.detail.label')}</p>
          <h1 class="page-hero-title">${escapeHtml(service.title)}</h1>
          <p class="page-hero-intro">${escapeHtml(detail.intro)}</p>
          <div class="service-hero-meta">
            ${detail.meta.map((m) => `<span class="service-pill">${escapeHtml(m)}</span>`).join('')}
          </div>
          <div class="page-hero-actions">
            <a href="/contact-us" class="btn-primary">${t('service.detail.start')}</a>
            <a href="/services" class="btn-secondary">${t('nav.services')}</a>
          </div>
        </div>
        <div class="page-hero-aside animate-on-scroll">
          <p class="page-hero-aside-kicker">Included in scope</p>
          <div class="page-hero-stack">
            ${detail.meta.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}
            <span>Launch support</span>
          </div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <div class="section-head animate-on-scroll">
          <p class="section-kicker">${t('service.detail.how')}</p>
          <h2 class="section-title">${t('service.detail.how')}</h2>
          <p class="section-intro">${escapeHtml(detail.approachIntro)}</p>
        </div>
        <div class="timeline">
          ${detail.approachSteps
            .map(
              (step, i) => `
            <div class="timeline-step animate-on-scroll" style="animation-delay: ${i * 0.06}s">
              <div class="timeline-marker">${String(i + 1).padStart(2, '0')}</div>
              <div class="timeline-content">
                <h3>${escapeHtml(step.title)}</h3>
                <p>${escapeHtml(step.description)}</p>
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    </section>
    <section class="section section-premium">
      <div class="container">
        <div class="section-head animate-on-scroll">
          <p class="section-kicker">${t('service.detail.lifecycle')}</p>
          <h2 class="section-title light">${t('service.detail.lifecycle')}</h2>
          <p class="section-intro light">${escapeHtml(detail.lifecycleIntro)}</p>
        </div>
        <div class="lifecycle-grid">
          ${detail.lifecycleSteps
            .map(
              (step, i) => `
            <div class="lifecycle-card animate-on-scroll" style="animation-delay: ${(i % 4) * 0.08}s">
              <div class="lifecycle-index">${String(i + 1).padStart(2, '0')}</div>
              <h3>${escapeHtml(step.title)}</h3>
              <p>${escapeHtml(step.description)}</p>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    </section>
    <section class="section cta-section">
      <div class="container cta-panel animate-on-scroll">
        <div>
          <p class="section-kicker">${t('service.detail.label')}</p>
          <h2 class="section-title light">${t('service.detail.ready', { service: escapeHtml(service.title) })}</h2>
          <p class="section-intro light">${t('service.detail.ready.body')}</p>
        </div>
        <div class="cta-panel-actions">
          <a href="/contact-us" class="btn-primary">${t('service.detail.ready.cta')}</a>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <div class="section-head animate-on-scroll">
          <p class="section-kicker">${t('service.detail.other.title')}</p>
          <h2 class="section-title">${t('service.detail.other.title')}</h2>
          <p class="section-intro">${t('service.detail.other.body')}</p>
        </div>
        <div class="services-showcase">
          ${services
            .filter((s) => s.slug !== service.slug)
            .map(
              (s, i) => {
                const slugParam = encodeURIComponent(s.slug);
                const imageUrl = getServiceImageUrl(s);
                return `
            <article class="service-card animate-on-scroll" style="animation-delay: ${(i % 3) * 0.05}s">
              <div class="service-card-visual" aria-hidden="true">
                ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" />` : ''}
                <div class="service-card-icon">${iconSvg[s.icon] ?? iconSvg.web}</div>
              </div>
              <div class="service-card-body">
                <h3>${escapeHtml(s.title)}</h3>
                <p>${escapeHtml(s.description)}</p>
                <a href="/services/${slugParam}" class="text-link service-card-cta">${t('service.detail.other.cta')}</a>
              </div>
            </article>
          `
              }
            )
            .join('')}
        </div>
      </div>
    </section>
  `;
}
